import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { typeLabels } from './saleConstants';

const DeliverableMatrix = ({
  opportunities,
  saleOwnerDeliverables,
  opportunityOwners,
  ownerEntities,
  saleId,
}) => {
  const navigate = useNavigate();

  // Get all horse deliverables for all opportunities
  const allHorseDeliverables = opportunities.flatMap(opp => opp.deliverables || []);

  // Group horse deliverables by type
  const horseDeliverableTypes = {};
  allHorseDeliverables.forEach(d => {
    if (d.deliverable_type_horse) {
      if (!horseDeliverableTypes[d.deliverable_type_horse]) {
        horseDeliverableTypes[d.deliverable_type_horse] = { total: 0, completed: 0, in_progress: 0, not_started: 0 };
      }
      horseDeliverableTypes[d.deliverable_type_horse].total++;
      if (d.status === 'completed') horseDeliverableTypes[d.deliverable_type_horse].completed++;
      else if (d.status === 'in_progress') horseDeliverableTypes[d.deliverable_type_horse].in_progress++;
      else horseDeliverableTypes[d.deliverable_type_horse].not_started++;
    }
  });

  // Group sale-level owner deliverables by type
  const ownerDeliverableTypes = {};
  const saleOwnerDels = saleOwnerDeliverables || [];
  saleOwnerDels.forEach(d => {
    if (!ownerDeliverableTypes[d.deliverable_type]) {
      ownerDeliverableTypes[d.deliverable_type] = { total: 0, completed: 0, in_progress: 0, not_started: 0 };
    }
    ownerDeliverableTypes[d.deliverable_type].total++;
    if (d.status === 'completed') ownerDeliverableTypes[d.deliverable_type].completed++;
    else if (d.status === 'in_progress') ownerDeliverableTypes[d.deliverable_type].in_progress++;
    else ownerDeliverableTypes[d.deliverable_type].not_started++;
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Horse Deliverables */}
      <div className="space-y-2">
        <p className="text-xs font-semibold mb-2">Horse Deliverables</p>
        {Object.keys(horseDeliverableTypes).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No horse deliverables</p>
        ) : (
          <div className="space-y-1">
            {Object.entries(horseDeliverableTypes).map(([type, stats]) => {
              const gapsCount = stats.total - stats.completed;
              const horsesWithGaps = opportunities
                .filter(opp => {
                  const deliverable = (opp.deliverables || []).find(d => d.deliverable_type_horse === type);
                  return deliverable && deliverable.status !== 'completed';
                })
                .map(opp => ({
                  horseName: opp.opportunity_name?.split(' - ')[1] || 'Unknown',
                  oppId: opp.id,
                  status: (opp.deliverables || []).find(d => d.deliverable_type_horse === type)?.status
                }));

              return (
                <Collapsible key={type}>
                  <div className="flex items-center justify-between p-2 bg-muted/30 rounded text-xs">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="font-medium">{typeLabels[type] || type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">{stats.completed}</span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-muted-foreground">{stats.total}</span>
                      {gapsCount > 0 && (
                        <>
                          <Badge variant="outline" className="ml-2 text-xs bg-gray-100">
                            {gapsCount} {gapsCount === 1 ? 'gap' : 'gaps'}
                          </Badge>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </CollapsibleTrigger>
                        </>
                      )}
                    </div>
                  </div>
                  {gapsCount > 0 && (
                    <CollapsibleContent className="pl-4 pt-1">
                      <div className="space-y-1 py-2">
                        {horsesWithGaps.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs p-2 bg-white cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/sales/opportunities/${item.oppId}`)}
                          >
                            <span className="text-muted-foreground">{item.horseName}</span>
                            <Badge variant="outline" className="text-xs">
                              {item.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              );
            })}
          </div>
        )}
      </div>

      {/* Owner Deliverables */}
      <div className="space-y-2">
        <p className="text-xs font-semibold mb-2">Owner Deliverables</p>
        {Object.keys(ownerDeliverableTypes).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No owner deliverables</p>
        ) : (
          <div className="space-y-1">
            {Object.entries(ownerDeliverableTypes).map(([type, stats]) => {
              const gapsCount = stats.total - stats.completed;
              const deliverablesOfType = saleOwnerDels.filter(d => d.deliverable_type === type && d.status !== 'completed');

              return (
                <Collapsible key={type}>
                  <div className="flex items-center justify-between p-2 bg-muted/30 rounded text-xs">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="font-medium">{typeLabels[type] || type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">{stats.completed}</span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-muted-foreground">{stats.total}</span>
                      {gapsCount > 0 && (
                        <>
                          <Badge variant="outline" className="ml-2 text-xs bg-gray-100">
                            {gapsCount} {gapsCount === 1 ? 'gap' : 'gaps'}
                          </Badge>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </CollapsibleTrigger>
                        </>
                      )}
                    </div>
                  </div>
                  {gapsCount > 0 && deliverablesOfType.length > 0 && (
                    <CollapsibleContent className="pl-4 pt-1">
                      <div className="space-y-1 py-2">
                        {deliverablesOfType.map((deliverable, idx) => {
                          const ownerName = ownerEntities[deliverable.owner_crm_entity_id]?.system_name || 'Owner';
                          const ownerOpp = opportunities.find(opp =>
                            opp.owner_crm_entity_ids?.includes(deliverable.owner_crm_entity_id)
                          );

                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-xs p-2 bg-white cursor-pointer hover:bg-muted/50"
                              onClick={() => ownerOpp && navigate(`/sales/opportunities/${ownerOpp.id}`)}
                            >
                              <div>
                                <span className="text-muted-foreground font-medium">{ownerName}</span>
                                {deliverable.property_line && (
                                  <>
                                    <span className="text-muted-foreground/60 mx-1">&bull;</span>
                                    <span className="text-muted-foreground/80 text-xs">{deliverable.property_line}</span>
                                  </>
                                )}
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {deliverable.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliverableMatrix;
